import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { faChevronRight, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { IAnamneseAnswer } from 'src/app/models/anamnese-form-answer.model';
import { IAnamneseQuestion, AnamneseQuestionType, AnamneseQuestionOptionDto } from 'src/app/models/anamnese-form-question.model';
import { IError } from 'src/app/models/error.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { AnamneseService } from 'src/app/services/anamnese.service';
import { AuthService } from 'src/app/services/auth.service';
import { IPatient } from 'src/app/models/patient.model';
import { IAnamneseDetail } from 'src/app/models/anamnese.model';
import { ICountriesDDI } from 'src/app/models/countries-ddi.model';
import { countriesDdi } from 'src/app/models/countries_ddi';
import { CompanyService } from 'src/app/services/company.service';
import { IAnamneseSendAnswers } from 'src/app/models/anamnese-sendanswers.model';
import { DocumentUtilsService } from 'src/app/services/document-utils.service';
import { PostalCodeService } from 'src/app/services/postal-code.service';
import { HyperDxService } from 'src/app/services/hyperdx.service';
import { AnamneseSectionDto } from 'src/app/models/anamnese-section.model';
import { AnamneseCacheService } from 'src/app/services/anamnese-cache.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { ModulesService } from 'src/app/services/modules.service';
import { IModule } from 'src/app/models/module.model';

/**
 * SUPORTE A HTML NOS LABELS DAS OPÇÕES:
 * 
 * As opções dos checkboxes e radio buttons agora suportam HTML através do [innerHTML].
 * Para formatar exemplos em itálico abaixo do texto principal, use a classe 'example-text':
 * 
 * Exemplo no banco de dados:
 * "Doenças de pele (dermatológicas)<br><span class='example-text'>(como psoríase, dermatite atópica, vitiligo, rosácea)</span>"
 * 
 * Resultado visual:
 * Doenças de pele (dermatológicas)
 * (como psoríase, dermatite atópica, vitiligo, rosácea) <- em itálico e menor
 * 
 * Tags HTML suportadas: <br>, <span>, <strong>, <em>, <i>, <b>, <small>, etc.
 */
@Component({
  selector: 'app-anamnese-answers',
  templateUrl: './anamnese-answers.component.html',
  styleUrls: ['./anamnese-answers.component.sass']
})
export class AnamneseAnswersComponent implements OnInit, OnDestroy {

  // Step control
  currentStep = 0; // 0 for patient data, 1+ for sections
  totalSteps = 1; // Starts with 1 for patient data, will be updated

  faChevronRight = faChevronRight;
  faCircleXmark = faCircleXmark;

  // Exposing Validators for template
  validators = Validators;

  anamneseId: string | null = '';
  anamneseForm = new FormGroup<{
    [key: string]: any;
  }>({});
  sections: AnamneseSectionDto[] = [];
  allQuestions: IAnamneseQuestion[] = [];

  patientId: string = '';

  permitedAuth: string[] = ['ADMIN', 'ATTENDANT']
  alreadyAnswered = false;
  loading = false;
  showError = false;

  // Properties for anamnese listing and sorting
  pageSize = 10;
  sortBy = 'DESC';
  orderBy = 'createdAt';

  isPatientUser = true;
  patientName = '';
  patientEmail = '';
  patientPhoneNumber = '';
  patient: IPatient | null = null;
  patientAge: number | null = null;
  isMinor = false; // Flag para indicar se o paciente é menor de idade
  countriesDdi = countriesDdi;
  filteredPhoneCountries = countriesDdi;
  filteredGuardianPhoneCountries = countriesDdi;
  filteredCountries = countriesDdi;
  isDropdownCountryOpen = false;
  isDropdownPhoneOpen = false;
  isDropdownGuardianPhoneOpen = false;

  viewModules$: Observable<{ moduleName: string; moduleNamePascalCase: string; moduleNameInSingular: string; }>;


  private initialValues: { [key: string]: any } = {};
  private cacheSubscription: Subscription | null = null;
  private hasLoadedFromCache = false;

  // Garante que o FormControl de país do telefone tenha um objeto válido (e não 'undefined')
  private normalizePhoneCountryControl(): void {
    const ctrl = this.anamneseForm.controls['patient.phoneCountry'];
    if (!ctrl) return;

    let value = ctrl.value;

    const isInvalid =
      value === undefined ||
      value === null ||
      value === '' ||
      (typeof value === 'string' && value.trim().toLowerCase() === 'undefined') ||
      (typeof value === 'string' && value.trim() === '') ||
      (typeof value === 'object' && value !== null && !('pais' in value));

    if (!isInvalid) {
      // Caso tenha vindo como string com um DDI ou nome de país, tentamos resolver
      if (typeof value === 'string') {
        const byString =
          this.countriesDdi.find(c => c.ddi === value || c.pais === value) || this.getDefaultCountryDdiObject();
        ctrl.setValue(byString);
        ctrl.updateValueAndValidity({ emitEvent: false });
      }
      return;
    }

    // Tenta mapear pelo valor do paciente, se existir
    const code = this.patient?.phone_country_code;
    if (code) {
      const found = this.countriesDdi.find(c => c.pais === code) || this.countriesDdi.find(c => c.ddi === code);
      if (found) {
        ctrl.setValue(found);
        ctrl.updateValueAndValidity({ emitEvent: false });
        return;
      }
    }

    // Fallback para Brasil
    const defaultObj = this.getDefaultCountryDdiObject();
    ctrl.setValue(defaultObj);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Configura a identificação do paciente no HyperDX para usuários não logados
   */
  private setPatientIdentificationInHyperDX(): void {
    try {
      if (this.isPatientUser && this.patient) {
        // Para usuários não logados preenchendo anamnese, configura identificação global
        this.hyperDxService.setPatientIdentification(
          this.patient.full_name || 'Paciente não identificado',
          this.patient.email || 'email_nao_disponivel@westetics.com.br',
          this.anamneseId || 'anamnese-unknown'
        );

        // Registra o evento de acesso
        this.hyperDxService.logAction('anamnese-patient-access', {
          userId: `patient-${this.anamneseId}`,
          userName: `Paciente: ${this.patient.full_name}`,
          userEmail: `${this.patient.full_name} <${this.patient.email || ''}>`,
          patientName: this.patient.full_name || 'Paciente não identificado',
          patientEmail: this.patient.email || '',
          anamneseId: this.anamneseId,
          userType: 'patient-filling-anamnese',
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Erro ao configurar identificação do paciente no HyperDX:', error);
    }
  }

  constructor(
    private anamneseService: AnamneseService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private store: Store<{ warningPopup: IError }>,
    private cdRef: ChangeDetectorRef,
    private documentUtils: DocumentUtilsService,
    private postalCodeService: PostalCodeService,
    private hyperDxService: HyperDxService,
    private cacheService: AnamneseCacheService,
    public modulesService: ModulesService
  ) {
    if (this.router.url.includes('answers')) {
      if (authService.isLogged() && this.permitedAuth.includes(authService.getPermission())) {
        this.isPatientUser = false;
      }
      else {
        authService.clear();
      }
    }
    this.viewModules$ = combineLatest({
      moduleName: this.modulesService.moduleName$,
      moduleNamePascalCase: this.modulesService.moduleNamePascalCase$,
      moduleNameInSingular: this.modulesService.moduleNameInSingular$
    });
  }

  ngOnInit(): void {
    try {
      this.loading = true;

      if (!this.anamneseForm) {
        this.anamneseForm = new FormGroup({});
      }

      this.anamneseForm.addControl('patient.full_name', new FormControl('', [Validators.required]));
      this.anamneseForm.addControl('patient.email', new FormControl('', [Validators.required, Validators.email]));
      this.anamneseForm.addControl('patient.phone', new FormControl('', [Validators.required, Validators.minLength(8)]));
      this.anamneseForm.addControl('patient.phoneCountry', new FormControl(this.getDefaultCountryDdiObject(), [Validators.required]));
      this.anamneseForm.addControl('patient.birth_date', new FormControl('', [Validators.required, this.birthDateValidator]));
      this.anamneseForm.addControl('patient.marital_status', new FormControl('', [Validators.required]));
      this.anamneseForm.addControl('patient.profession', new FormControl('', [Validators.required]));
      this.anamneseForm.addControl('patient.is_foreigner', new FormControl(false));
      this.anamneseForm.addControl('patient.rg', new FormControl('', [Validators.required]));
      this.anamneseForm.addControl('patient.cpf', new FormControl('', [this.modulesService.moduleName === 'ALUNOS' ? this.documentUtils.cpfCnpjValidator() : this.documentUtils.cpfValidator()]));
      this.anamneseForm.addControl('patient.image_auth', new FormControl(false, []));
      this.anamneseForm.addControl('patient.instagram_account', new FormControl('', []));

      // Campos do responsável legal (para menores de idade)
      this.anamneseForm.addControl('patient.guardian_name', new FormControl('', []));
      this.anamneseForm.addControl('patient.guardian_cpf', new FormControl('', []));
      this.anamneseForm.addControl('patient.guardian_phone', new FormControl('', []));
      this.anamneseForm.addControl('patient.guardian_phone_country', new FormControl(this.getDefaultCountryDdiObject(), []));

      this.anamneseForm.addControl('patient.address.street', new FormControl('', [Validators.required]));
      this.anamneseForm.addControl('patient.address.number', new FormControl('', [Validators.required]));
      this.anamneseForm.addControl('patient.address.complement', new FormControl(''));
      this.anamneseForm.addControl('patient.address.neighborhood', new FormControl('', [Validators.required]));
      this.anamneseForm.addControl('patient.address.city', new FormControl('', [Validators.required]));
      this.anamneseForm.addControl('patient.address.state', new FormControl('', [Validators.required]));
      this.anamneseForm.addControl('patient.address.zip_code', new FormControl('', [this.postalCodeService.postalCodeValidator(true)]));
      this.anamneseForm.addControl('patient.address.country', new FormControl(this.getDefaultCountryDdiObject().pais, [Validators.required]));

      // Listener para atualizar validadores do responsável quando a data de nascimento mudar
      this.anamneseForm.controls['patient.birth_date']?.valueChanges.subscribe((birthDate: string) => {
        this.updateGuardianValidators(birthDate);
      });

      this.anamneseForm.controls['patient.is_foreigner']?.valueChanges.subscribe((isForeigner: boolean) => {
        const rgControl = this.anamneseForm.controls['patient.rg'];
        const cpfControl = this.anamneseForm.controls['patient.cpf'];

        if (isForeigner) {
          rgControl?.clearValidators();
          cpfControl?.clearValidators();
        } else {
          rgControl?.setValidators([Validators.required]);
          cpfControl?.setValidators([Validators.required, this.modulesService.moduleName === 'ALUNOS' ? this.documentUtils.cpfCnpjValidator() : this.documentUtils.cpfValidator()]);
        }

        rgControl?.updateValueAndValidity();
        cpfControl?.updateValueAndValidity();
        this.updatePostalCodePlaceholder();
      });

      this.anamneseForm.controls['patient.address.country']?.valueChanges.subscribe((country: string) => {
        this.updatePostalCodePlaceholder();
        const zipCodeControl = this.anamneseForm.controls['patient.address.zip_code'];
        zipCodeControl.setValidators([this.postalCodeService.postalCodeValidator(this.postalCodeService.isBrazil(country))]);
        zipCodeControl.updateValueAndValidity();
      });

      this.patientId = this.activatedRoute.snapshot.queryParams['patientId'] || '';

      this.activatedRoute.params.subscribe(params => {
        this.anamneseId = params['anamneseId'];
        if (!this.anamneseId) {
          console.error('ID da anamnese não encontrado nos parâmetros da rota.');
          this.store.dispatch(setWarningText("ID da anamnese não fornecido. Não é possível carregar o formulário."));
          this.router.navigate(['/anamnese-invalid']);
          this.loading = false;
          return;
        }

        this.anamneseService.getQuestions(this.anamneseId)
          .then((sectionsResponse) => {
            if (!sectionsResponse || !Array.isArray(sectionsResponse)) {
              throw new Error('Formato inválido de seções recebido do servidor');
            }
            this.sections = sectionsResponse;
            this.allQuestions = [];
            this.ordenarSecoesEPerguntas();
            this.totalSteps = 1 + (this.sections ? this.sections.length : 0);
            this.sections.forEach(section => {
              if (section && section.questions && Array.isArray(section.questions)) {
                section.questions.forEach(question => {
                  if (!question || !question.id) {
                    console.error('Questão inválida recebida do servidor dentro da seção:', section.name, question);
                  } else {
                    this.allQuestions.push(question);
                    this.addQuestion(question);
                  }
                });
              }
            });
            return this.getAnamnese();
          })
          .then(() => {
            if (this.patient) {
              try {
                Object.keys(this.anamneseForm.controls)
                  .filter(key => key.startsWith('patient.'))
                  .forEach(key => {
                    const control = this.anamneseForm.controls[key];
                    const initialValue = this.initialValues[key];

                    if (control && initialValue !== undefined && initialValue !== null && initialValue.toString().trim() !== '') {
                      control.markAsTouched();

                      const elementId = key.split('.').pop() || '';
                      const element = document.querySelector(`[id="${elementId}"]`);
                      if (element) {
                        element.addEventListener('blur', () => {
                          const currentValue = control.value;
                          if ((currentValue === null || currentValue === undefined || currentValue.toString().trim() === '') &&
                            !((key === 'patient.rg' || key === 'patient.cpf') && this.anamneseForm.controls['patient.is_foreigner'].value)) {
                            control.setValue(initialValue, { emitEvent: false });
                            control.markAsTouched();

                            const fieldName = this.getFieldDisplayName(key);
                            this.store.dispatch(setWarningText(`O campo ${fieldName} não pode ficar em branco pois já possui um valor cadastrado.`));
                          }
                        });
                      }

                      if (!((key === 'patient.rg' || key === 'patient.cpf') && this.anamneseForm.controls['patient.is_foreigner'].value)) {
                        const existingValidators = control.validator ? (Array.isArray(control.validator) ? control.validator : [control.validator]) : [];
                        let needsRequired = true;
                        existingValidators.forEach((validator: import('@angular/forms').ValidatorFn) => {
                          if (validator === Validators.required) needsRequired = false;
                        });
                        if (needsRequired) {
                          control.setValidators([...existingValidators, Validators.required]);
                        }
                      }
                    } else if (control && control.validator === null && !((key === 'patient.rg' || key === 'patient.cpf') && this.anamneseForm.controls['patient.is_foreigner'].value)) {
                    }
                    control.updateValueAndValidity({ emitEvent: false });
                  });

                // Bloqueia edição de Data de Nascimento, RG e CPF em anamneses pré-preenchidas
                const lockableFields = [
                  'patient.birth_date',
                  'patient.rg',
                  'patient.cpf'
                ];

                lockableFields.forEach(fieldKey => {
                  const control = this.anamneseForm.controls[fieldKey];
                  const initialValue = this.initialValues[fieldKey];

                  // Só bloqueia se existir valor inicial (dado vindo do cadastro do paciente)
                  if (control && initialValue !== undefined && initialValue !== null && initialValue.toString().trim() !== '') {
                    control.disable({ emitEvent: false });
                  }
                });

                if (this.patientId !== '' && !this.isPatientUser) {
                  Object.keys(this.anamneseForm.controls)
                    .filter(key => key.startsWith('patient.'))
                    .forEach(key => this.anamneseForm.controls[key].disable());
                }

                // Após configurar campos e validadores, normaliza phoneCountry
                this.normalizePhoneCountryControl();

              } catch (e) {
                console.error('Erro ao configurar validadores pós-carregamento do paciente:', e);
                this.store.dispatch(setWarningText("Erro ao configurar validações do formulário do paciente."));
              }
            }
          })
          .catch((error) => {
            console.error('Erro na cadeia de inicialização (getQuestions ou getAnamnese):', error);
            if (error && error.status === 404) {
              this.router.navigate(['/anamnese-invalid']);
            } else if (error && error.message && error.message.includes('ID da anamnese não encontrado')) {
            } else {
              this.store.dispatch(setWarningText("Erro ao carregar dados da anamnese. Tente recarregar a página."));
            }
          })
          .finally(() => {
            this.loading = false;
            this.cdRef.detectChanges();

            // Configurar cache automático após carregamento completo
            if (this.isPatientUser && this.anamneseId) {
              this.setupAutomaticCache();
              this.setModuleByPatientInfo();
              this.updateCpfValidatorForModule();
            }
          });
      });

    } catch (error) {
      console.error('Erro síncrono na inicialização do ngOnInit:', error);
      this.store.dispatch(setWarningText("Erro crítico ao inicializar o formulário. Por favor, recarregue a página."));
      this.loading = false;
      this.cdRef.detectChanges();
    }
  }

  private setModuleByPatientInfo() {
    this.authService.setSelectedModule(this.patient?.module as IModule || { id: '', description: '' });
  }

  private updateCpfValidatorForModule() {
    const cpfControl = this.anamneseForm.controls['patient.cpf'];
    if (!cpfControl) return;
    const isForeigner = this.anamneseForm.controls['patient.is_foreigner']?.value;
    if (isForeigner) return; // foreigners have no validators
    const validator = this.modulesService.moduleName === 'ALUNOS'
      ? this.documentUtils.cpfCnpjValidator()
      : this.documentUtils.cpfValidator();
    cpfControl.setValidators([Validators.required, validator]);
    cpfControl.updateValueAndValidity();
  }

  /**
   * Ordena as seções e perguntas conforme a ordem definida
   */
  private ordenarSecoesEPerguntas() {
    if (!this.sections || this.sections.length === 0) return;

    // Ordena as seções pelo campo order
    this.sections.sort((a, b) => a.order - b.order);

    // Ordena as perguntas em cada seção pelo campo order_in_section
    this.sections.forEach(section => {
      if (section.questions && Array.isArray(section.questions)) {
        section.questions.sort((a, b) => a.order_in_section - b.order_in_section);
      }
    });
  }

  private scrollToFirstInvalidField(fieldId: string) {
    setTimeout(() => {
      if (!fieldId) {
       
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        return;
      }

      const fieldMapping: { [key: string]: string } = {
        'full_name': 'full_name',
        'email': 'email',
        'phone': 'phone',
        'birth_date': 'birth_date',
        'marital_status': 'marital_status',
        'profession': 'profession',
        'rg': 'rg',
        'cpf': 'cpf',
        'address.street': 'street',
        'address.number': 'number',
        'address.complement': 'complement',
        'address.neighborhood': 'neighborhood',
        'address.city': 'city',
        'address.state': 'state',
        'address.zip_code': 'zipCode',
        'address.country': 'country',
        'guardian_name': 'guardian_name',
        'guardian_cpf': 'guardian_cpf',
        'guardian_phone': 'guardian_phone'
      };

      const elementId = fieldMapping[fieldId] || fieldId;

      // Tentar diferentes seletores para encontrar o elemento
      let element = document.querySelector(`[id="${elementId}"]`) ||
        document.querySelector(`#${elementId}`) ||
        document.querySelector(`[name="${elementId}"]`) ||
        document.querySelector(`[formControlName="${fieldId}"]`) ||
        document.querySelector(`[formControlName="${elementId}"]`);

      if (element) {
        // Encontrou o elemento, rolar até ele
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        // Tentar focar no elemento se for um input
        if (element instanceof HTMLInputElement ||
          element instanceof HTMLSelectElement ||
          element instanceof HTMLTextAreaElement) {
          setTimeout(() => {
            (element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).focus();
          }, 500); // Pequeno delay para permitir que o scroll termine
        }

     
      } else {
        // Elemento não encontrado, tentar rolar para o primeiro campo inválido visível
        const invalidFields = document.querySelectorAll('.ng-invalid:not(form):not(.ng-pristine)');
        if (invalidFields.length > 0) {
          const firstInvalidField = invalidFields[0] as HTMLElement;
          firstInvalidField.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
          
        } else {
          // Fallback: rolar para o topo
          console.warn(`[scrollToFirstInvalidField] Tentativa de scroll para o campo com ID '${elementId}' (originalmente de fieldId '${fieldId}'), mas o elemento não foi encontrado. Rolando para o topo.`);
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    }, 100);
  }

  addQuestion(question: IAnamneseQuestion) {
    try {
      if (!question || !question.id) {
        throw new Error('Questão inválida');
      }

      if (this.anamneseForm.contains(question.id + 'answer') ||
        this.anamneseForm.contains(question.id + 'observation')) {
        console.warn('Tentativa de adicionar questão duplicada ao formulário:', question.id);
        return;
      }

      const answerValidators = [];
      if (question.is_required) {
        answerValidators.push(Validators.required);
      }

      let initialAnswerValue: any = null;
      switch (question.type) {
        case 'MULTIPLE_CHOICE':
          initialAnswerValue = [];
          break;
        case 'YES_NO':
          initialAnswerValue = null; // Será convertido para boolean se question.answer existir
          break;
        case 'TEXT_INPUT':
        case 'SINGLE_CHOICE':
        // Adicionar 'SELECT' e 'TEXT' se forem tipos válidos e precisarem de string vazia como default
        case 'SELECT':
        case 'TEXT':
        default:
          initialAnswerValue = '';
          break;
      }

      if (question.answer !== undefined && question.answer !== null) {
        if (question.type === 'YES_NO') {
          // Converte "sim"/"não" para true/false para o formulário
          const answerStr = String(question.answer).toLowerCase();
          initialAnswerValue = answerStr === "sim" || answerStr === "true";
        }
        else {
          initialAnswerValue = question.answer;
        }
      }

      this.anamneseForm.addControl(question.id + 'answer', new FormControl(initialAnswerValue, answerValidators));

      const initialObservationValue = question.observation || '';
      this.anamneseForm.addControl(question.id + 'observation', new FormControl(initialObservationValue));

      // Configurar validação dinâmica para o campo de observação
      this.setupObservationValidation(question);

      if (this.isPatientUser) {
        // Se for paciente e já respondido (alreadyAnswered), desabilitar.
        // No entanto, alreadyAnswered pode não estar definido ainda aqui.
        // A desabilitação principal para visualização ocorre no HTML com [disabled] ou no else abaixo.
      } else { // Admin/Atendente view
        const answerControl = this.anamneseForm.controls[question.id + 'answer'];
        const observationControl = this.anamneseForm.controls[question.id + 'observation'];

        // Na visão do admin/atendente, os campos são sempre desabilitados para visualização.
        if (answerControl) {
          answerControl.disable();
        }
        if (observationControl) {
          observationControl.disable();
        }
      }
    } catch (error) {
      console.error('Erro ao adicionar questão:', question.label, error);
    }
  }

  // Novo método para configurar validação dinâmica da observação
  private setupObservationValidation(question: IAnamneseQuestion) {
    const answerControl = this.anamneseForm.controls[question.id + 'answer'];
    const observationControl = this.anamneseForm.controls[question.id + 'observation'];

    if (!answerControl || !observationControl || !question.options) {
      return;
    }

    // Escutar mudanças na resposta para atualizar validação da observação
    answerControl.valueChanges.subscribe((value: any) => {
      this.updateObservationValidation(question.id, question.type, question.options || [], value);
    });

    // Aplicar validação inicial se já há uma resposta
    if (answerControl.value !== null && answerControl.value !== undefined) {
      this.updateObservationValidation(question.id, question.type, question.options || [], answerControl.value);
    }
  }

  // Novo método para atualizar validação da observação baseado na resposta
  private updateObservationValidation(questionId: string, questionType: string, options: AnamneseQuestionOptionDto[], answerValue: any) {
    const observationControl = this.anamneseForm.controls[questionId + 'observation'];
    if (!observationControl) return;

    let shouldRequireObservation = false;

    // Regra geral: quando o campo de observação estiver visível/aberto, ele deve ser obrigatório.
    // Critérios de "campo aberto" por tipo:
    // - YES_NO: qualquer resposta selecionada (true/false) abre o campo => obrigatório
    // - SINGLE_CHOICE: se a opção selecionada tiver triggersFreeText => obrigatório
    // - MULTIPLE_CHOICE: se alguma opção selecionada tiver triggersFreeText => obrigatório
    // - Outros tipos: manter comportamento atual (sem obrigatoriedade automática)

    if (questionType === 'YES_NO') {
      // Quando o usuário seleciona Sim/Não, o campo de observação aparece; tornar obrigatório
      shouldRequireObservation = answerValue !== null && answerValue !== undefined;
    } else if (questionType === 'MULTIPLE_CHOICE' && Array.isArray(answerValue)) {
      shouldRequireObservation = options.some(option =>
        answerValue.includes(option.value) && option.triggersFreeText === true
      );
    } else if (questionType === 'SINGLE_CHOICE' && answerValue) {
      const selectedOption = options.find(option => option.value === answerValue);
      shouldRequireObservation = selectedOption?.triggersFreeText === true;
    }

    // Aplicar ou remover validação de campo obrigatório
    if (shouldRequireObservation) {
      observationControl.setValidators([Validators.required]);
    } else {
      observationControl.clearValidators();
    }

    observationControl.updateValueAndValidity();
  }

  // Método para verificar se a pergunta está na seção de saúde
  private isQuestionInHealthSection(questionId: string): boolean {
    for (const section of this.sections) {
      if (section.questions?.some(q => q.id === questionId)) {
        // Verificar se é a seção de questões de saúde (pode ser por nome ou ordem)
        const sectionName = section.name?.toLowerCase() || '';
        const isHealthSectionByName = sectionName.includes('saúde') ||
          sectionName.includes('questões de saúde') ||
          sectionName.includes('condições de saúde') ||
          sectionName.includes('health');

        // Ou verificar se é a seção de ordem 5 (assumindo que a contagem começa em 1)
        const isHealthSectionByOrder = section.order === 5;

        return isHealthSectionByName || isHealthSectionByOrder;
      }
    }
    return false;
  }

  private calculateAge(birthDateString: string | null | undefined): number | null {
    if (!birthDateString) {
      return null;
    }

    let birthDate: Date;
    // Tentar normalizar a data, comum vir como AAAA-MM-DD ou DD/MM/AAAA
    if (birthDateString.includes('-')) { // Formato AAAA-MM-DD
      const parts = birthDateString.split('-');
      birthDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    } else if (birthDateString.includes('/')) { // Formato DD/MM/AAAA
      const parts = birthDateString.split('/');
      birthDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
    } else {
      // Formato não reconhecido ou inválido
      console.error('Formato de data de nascimento não reconhecido:', birthDateString);
      return null;
    }

    if (isNaN(birthDate.getTime())) {
      console.error('Data de nascimento inválida após parse:', birthDateString);
      return null;
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age < 0 ? 0 : age; // Evitar idade negativa se data for futura
  }

  /**
   * Atualiza os validadores dos campos do responsável legal baseado na idade do paciente.
   * Se o paciente for menor de idade (< 18 anos), os campos do responsável são obrigatórios.
   */
  private updateGuardianValidators(birthDateString: string): void {
    const age = this.calculateAge(birthDateString);
    this.isMinor = age !== null && age < 18;

    const guardianNameControl = this.anamneseForm.controls['patient.guardian_name'];
    const guardianCpfControl = this.anamneseForm.controls['patient.guardian_cpf'];
    const guardianPhoneControl = this.anamneseForm.controls['patient.guardian_phone'];
    const guardianPhoneCountryControl = this.anamneseForm.controls['patient.guardian_phone_country'];

    if (this.isMinor) {
      // Paciente é menor de idade - campos do responsável são obrigatórios
      guardianNameControl?.setValidators([Validators.required]);
      guardianCpfControl?.setValidators([Validators.required, this.documentUtils.cpfCnpjValidator()]);
      guardianPhoneControl?.setValidators([Validators.required, Validators.minLength(8)]);
      guardianPhoneCountryControl?.setValidators([Validators.required]);
    } else {
      // Paciente é maior de idade - campos do responsável são opcionais
      guardianNameControl?.clearValidators();
      guardianCpfControl?.clearValidators();
      guardianPhoneControl?.clearValidators();
      guardianPhoneCountryControl?.clearValidators();
    }

    guardianNameControl?.updateValueAndValidity();
    guardianCpfControl?.updateValueAndValidity();
    guardianPhoneControl?.updateValueAndValidity();
    guardianPhoneCountryControl?.updateValueAndValidity();
  }

  getSendAnamneseButtonText() {
    return this.modulesService.moduleName === 'PACIENTES' ? 'Enviar Anamnese' : 'Enviar formulário';
  }

  getAnamnese() {
    return new Promise((resolve, reject) => {
      if (this.isPatientUser) {
        this.anamneseService.getAnamneseByIdPublic(this.anamneseId || '')
          .then((anamnese) => {
            this.alreadyAnswered = anamnese.is_answered || false;
            this.patient = anamnese.patient || null;

            if (this.patient) {
              this.patientAge = this.calculateAge(this.patient.birth_date);

              // Configura identificação do paciente no HyperDX
              this.setPatientIdentificationInHyperDX();

              // Atualiza a flag de menor de idade e os validadores do responsável
              this.updateGuardianValidators(this.patient.birth_date);

              const patientData: { [key: string]: any } = {
                'patient.full_name': this.patient.full_name || '',
                'patient.email': this.patient.email || '',
                'patient.phone': this.patient.phone || '',
                'patient.birth_date': this.formatDateToDisplay(this.patient.birth_date) || '',
                'patient.marital_status': this.patient.marital_status || '',
                'patient.profession': this.patient.profession || '',
                'patient.rg': this.patient.rg || '',
                'patient.cpf': this.patient.cpf || '',
                'patient.is_foreigner': this.patient.is_foreigner || false,
                'patient.image_auth': this.patient.image_auth || false,
                'patient.instagram_account': this.patient.instagram_account || '',
                // Mapeia phone_country_code tanto se vier como DDI (+55) quanto nome do país (Brasil)
                'patient.phoneCountry': this.patient.phone_country_code
                  ? (this.countriesDdi.find(c => c.pais === this.patient!.phone_country_code) ||
                    this.countriesDdi.find(c => c.ddi === this.patient!.phone_country_code) ||
                    this.getDefaultCountryDdiObject())
                  : this.getDefaultCountryDdiObject(),
                'patient.address.street': this.patient.address?.street || '',
                'patient.address.number': this.patient.address?.number || '',
                'patient.address.complement': this.patient.address?.complement || '',
                'patient.address.neighborhood': this.patient.address?.neighborhood || '',
                'patient.address.city': this.patient.address?.city || '',
                'patient.address.state': this.patient.address?.state || '',
                'patient.address.zip_code': this.patient.address?.zip_code || '',
                'patient.address.country': this.patient.address?.country || '',
                // Dados do responsável legal
                'patient.guardian_name': this.patient.guardian_name || '',
                'patient.guardian_cpf': this.patient.guardian_cpf || '',
                'patient.guardian_phone': this.patient.guardian_phone || '',
                'patient.guardian_phone_country': this.patient.guardian_phone_country_code
                  ? (this.countriesDdi.find(c => c.pais === this.patient!.guardian_phone_country_code) ||
                    this.countriesDdi.find(c => c.ddi === this.patient!.guardian_phone_country_code) ||
                    this.getDefaultCountryDdiObject())
                  : this.getDefaultCountryDdiObject()
              };


              Object.entries(patientData).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value.toString().trim() !== '') {
                  this.initialValues[key] = value;
                }
              });

              this.anamneseForm.patchValue(patientData);

              // Tentar recuperar dados do cache antes de continuar
              this.loadFromCacheIfAvailable();

              // Após patchValue e (possível) cache, normalize o país do telefone
              this.normalizePhoneCountryControl();

              // Configuração de validadores para campos de paciente com valores iniciais
              // Esta lógica foi movida para o .then() da cadeia principal do ngOnInit
              // para garantir que ocorra após os dados do paciente e as questões serem carregados.
            }
            resolve(true);
          })
          .catch((error) => {
            console.error('Erro ao carregar anamnese (public):', error);
            if (error && error.status === 404) {
              this.router.navigate(['/anamnese-invalid']);
            } else {
              this.store.dispatch(setWarningText("Erro ao carregar os dados do paciente. Verifique os dados ou tente novamente."));
            }
            reject(error);
          });
      } else { // Admin/Atendente view
        this.anamneseService.getAnamneseById(this.anamneseId || '')
          .then((anamnese) => {
            this.alreadyAnswered = anamnese.is_answered || false;
            this.patient = anamnese.patient || null;
            if (this.patient) {
              this.patientAge = this.calculateAge(this.patient.birth_date);

              // Para usuários logados (admin/atendente), registra acesso à anamnese do paciente
              if (!this.isPatientUser) {
                this.hyperDxService.logAction('anamnese-admin-access', {
                  patientName: this.patient.full_name || 'Paciente não identificado',
                  patientEmail: this.patient.email || 'email_nao_disponivel@westetics.com.br',
                  anamneseId: this.anamneseId,
                  userType: 'admin-viewing-anamnese',
                  adminUserId: this.authService.getUserId(),
                  adminUserName: this.authService.getUsername(),
                  timestamp: new Date().toISOString()
                });
              }
              this.anamneseForm.controls['patient.full_name']?.setValue(this.patient.full_name || '');
              this.anamneseForm.controls['patient.email']?.setValue(this.patient.email || '');
              this.anamneseForm.controls['patient.phone']?.setValue(this.patient.phone || '');
              const phoneCountryValue = this.patient.phone_country_code
                ? (this.countriesDdi.find(c => c.pais === this.patient!.phone_country_code) ||
                  this.countriesDdi.find(c => c.ddi === this.patient!.phone_country_code) ||
                  this.getDefaultCountryDdiObject())
                : this.getDefaultCountryDdiObject();
              this.anamneseForm.controls['patient.phoneCountry']?.setValue(phoneCountryValue);
              this.anamneseForm.controls['patient.birth_date']?.setValue(this.formatDateToDisplay(this.patient.birth_date) || '');
              this.anamneseForm.controls['patient.marital_status']?.setValue(this.patient.marital_status || '');
              this.anamneseForm.controls['patient.profession']?.setValue(this.patient.profession || '');
              this.anamneseForm.controls['patient.is_foreigner']?.setValue(this.patient.is_foreigner || false);
              this.anamneseForm.controls['patient.rg']?.setValue(this.patient.rg || '');
              this.anamneseForm.controls['patient.cpf']?.setValue(this.patient.cpf || '');
              this.anamneseForm.controls['patient.image_auth']?.setValue(this.patient.image_auth || false);
              this.anamneseForm.controls['patient.instagram_account']?.setValue(this.patient.instagram_account || '');
              if (this.patient.address) {
                this.anamneseForm.controls['patient.address.street']?.setValue(this.patient.address.street || '');
                this.anamneseForm.controls['patient.address.number']?.setValue(this.patient.address.number || '');
                this.anamneseForm.controls['patient.address.complement']?.setValue(this.patient.address.complement || '');
                this.anamneseForm.controls['patient.address.neighborhood']?.setValue(this.patient.address.neighborhood || '');
                this.anamneseForm.controls['patient.address.city']?.setValue(this.patient.address.city || '');
                this.anamneseForm.controls['patient.address.state']?.setValue(this.patient.address.state || '');
                this.anamneseForm.controls['patient.address.zip_code']?.setValue(this.patient.address.zip_code || '');
                this.anamneseForm.controls['patient.address.country']?.setValue(this.patient.address.country || '');
              }
              // Dados do responsável legal
              this.anamneseForm.controls['patient.guardian_name']?.setValue(this.patient.guardian_name || '');
              this.anamneseForm.controls['patient.guardian_cpf']?.setValue(this.patient.guardian_cpf || '');
              this.anamneseForm.controls['patient.guardian_phone']?.setValue(this.patient.guardian_phone || '');
              const guardianPhoneCountryValue = this.patient.guardian_phone_country_code
                ? (this.countriesDdi.find(c => c.pais === this.patient!.guardian_phone_country_code) ||
                  this.countriesDdi.find(c => c.ddi === this.patient!.guardian_phone_country_code) ||
                  this.getDefaultCountryDdiObject())
                : this.getDefaultCountryDdiObject();
              this.anamneseForm.controls['patient.guardian_phone_country']?.setValue(guardianPhoneCountryValue);
              // Atualiza flag de menor de idade
              this.updateGuardianValidators(this.patient.birth_date);
            }
            resolve(true);
          })
          .catch((error) => {
            console.error('Erro ao carregar anamnese (admin):', error);
            reject(error);
          });
      }
    });
  }

  getQuestionAnswer(question_id: string) {
    if (!this.anamneseForm.controls[question_id + 'answer']) return null;

    return this.anamneseForm.controls[question_id + 'answer']?.value;
  }

  /**
   * Obtém a resposta formatada para exibição de perguntas YES_NO
   * Converte true/false para "Sim"/"Não" para exibição
   */
  getYesNoAnswerDisplay(question_id: string): string | null {
    const answer = this.getQuestionAnswer(question_id);
    if (answer === null || answer === undefined) return null;

    // Se for string "sim"/"não", converte para "Sim"/"Não"
    if (typeof answer === 'string') {
      const answerLower = answer.toLowerCase();
      if (answerLower === 'sim') return 'Sim';
      if (answerLower === 'não' || answerLower === 'nao') return 'Não';
    }

    // Se for boolean, converte para "Sim"/"Não"
    if (typeof answer === 'boolean') {
      return answer ? 'Sim' : 'Não';
    }

    return null;
  }

  getQuestionObservation(question_id: string) {
    const observation = this.anamneseForm.controls[question_id + 'observation']?.value;
    return observation || '';
  }

  cancelButton() {
    if (this.patientId) {
      this.router.navigate(['main', 'patients', this.patientId, 'edit'], { queryParams: { headerSelectedOption: 'Anamnese' } });
    }
    else {
      this.router.navigate(['main', 'anamneses']);
    }
  }

  generateDocs() {
    if (!this.loading) {
      this.getLatestAnamneseId().then((anamneseId) => {
        if (anamneseId) {
          this.router.navigate(['main', 'anamneses', anamneseId, 'generate-docs'], {
            queryParams: {
              patientId: this.patientId,
              context: 'list',
              isModelPatient: this.patient?.is_model_patient || false
            },
          });
        }
      });
    }
  }

  async getLatestAnamneseId(): Promise<string | null> {
    try {
      const anamneses: IAnamneseDetail[] = await this.getAnamneses(0, this.pageSize);

      const answeredAnamneses = anamneses.filter(
        (anamnese) => anamnese.is_answered && anamnese.created_at
      );

      const latestAnamnese = answeredAnamneses.sort(
        (a, b) =>
          new Date(b.created_at as string).getTime() - new Date(a.created_at as string).getTime()
      )[0];
      return latestAnamnese && latestAnamnese.id ? latestAnamnese.id : null;
    } catch (error) {
      return null;
    }
  }

  getAnamneses(pageNumber: number, pageSize: number): Promise<IAnamneseDetail[]> {
    this.loading = true;
    return new Promise((resolve, reject) => {
      this.anamneseService
        .listAnamneses(pageNumber, pageSize, this.sortBy, this.orderBy, this.patientId || '', this.modulesService.moduleId || '')
        .subscribe({
          next: (response) => {
            resolve(response.data);
            this.loading = false;
          },
          error: (error) => {
            console.error('Error fetching anamneses:', error);
            reject(error);
            this.loading = false;
          }
        });
    });
  }

  private formatDateToDisplay(dateString: string): string {
    if (!dateString) return '';
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  }

  private birthDateValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;

    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      return { birthDateFormat: true };
    }

    const [day, month, year] = trimmed.split('/').map(Number);
    const currentYear = new Date().getFullYear();

    if (year < 1900 || year > currentYear) {
      return { birthDateYearOutOfRange: true };
    }

    const parsed = new Date(year, month - 1, day);
    if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
      return { birthDateInvalid: true };
    }

    return null;
  };

  private formatDateValue(dateString: string): string {
    if (!dateString) return '';
    const trimmed = dateString.trim();

    let day: string, month: string, year: string;

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      [year, month, day] = trimmed.split('-');
    } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      [day, month, year] = trimmed.split('/');
    } else if (/^\d{8}$/.test(trimmed)) {
      day = trimmed.slice(0, 2);
      month = trimmed.slice(2, 4);
      year = trimmed.slice(4, 8);
    } else {
      return '';
    }

    const d = Number(day), m = Number(month), y = Number(year);
    const currentYear = new Date().getFullYear();

    if (y < 1900 || y > currentYear) return '';
    if (m < 1 || m > 12) return '';
    if (d < 1 || d > 31) return '';

    const parsed = new Date(y, m - 1, d);
    if (parsed.getFullYear() !== y || parsed.getMonth() !== m - 1 || parsed.getDate() !== d) {
      return '';
    }

    return `${year}-${month}-${day}`;
  }

  submitAnamnese() {
    this.loading = true;

    try {
      if (!this.anamneseForm) {
        this.store.dispatch(setWarningText("Erro ao carregar o formulário. Por favor, recarregue a página."));
        this.loading = false;
        return;
      }

      if ((!this.sections || this.sections.length === 0) && this.totalSteps > 1) { // Checa se há seções se totalSteps indica que deveriam existir
        this.store.dispatch(setWarningText("Erro ao carregar as questões. Por favor, recarregue a página."));
        this.loading = false;
        return;
      }

      // Nova lógica de validação iterando por todos os steps
      for (let i = 0; i < this.totalSteps; i++) {
        if (!this.isStepValid(i)) {
          this.markStepControlsAsTouched(i);
          this.scrollToFirstInvalidFieldOfStep(i); // Rola para o topo e loga o primeiro erro do step
          this.store.dispatch(setWarningText("Existem campos obrigatórios não preenchidos ou inválidos. Verifique todas as etapas."));
          this.loading = false;
          this.showError = true;
          return;
        }
      }

      // Se todos os steps são válidos, prossegue com a coleta e envio dos dados
      const patientInfoValue = { ...this.patient };
      Object.keys(this.anamneseForm.controls).forEach(key => {
        if (key.startsWith('patient.')) {
          const fieldName = key.substring('patient.'.length);
          let value = this.anamneseForm.controls[key].value;

          if (fieldName === 'birth_date') {
            value = this.formatDateValue(value);
          }
          if (fieldName === 'phoneCountry') {
            value = value?.ddi;
          }
          // Mapeia guardian_phone_country para guardian_phone_country_code (DDI)
          if (fieldName === 'guardian_phone_country') {
            (patientInfoValue as any)['guardian_phone_country_code'] = value?.ddi || '';
            return; // Não continua para evitar criar o campo guardian_phone_country
          }

          const parts = fieldName.split('.');
          let current = patientInfoValue as any;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) {
              current[parts[i]] = {};
            }
            current = current[parts[i]];
          }
          current[parts[parts.length - 1]] = value;
        }
      });

      if (patientInfoValue.is_foreigner) {
        delete patientInfoValue.rg;
        delete patientInfoValue.cpf;
      } else {
        patientInfoValue.rg = patientInfoValue.rg || '';
        patientInfoValue.cpf = patientInfoValue.cpf || '';
      }

      const answersDto: IAnamneseAnswer[] = this.allQuestions
        .map(question => {
          const answerControl = this.anamneseForm.controls[question.id + 'answer'];
          let answerValue = answerControl ? answerControl.value : null;
          const observationControl = this.anamneseForm.controls[question.id + 'observation'];
          const observationValue = observationControl ? observationControl.value : '';

          switch (question.type) {
            case 'MULTIPLE_CHOICE':
              answerValue = Array.isArray(answerValue) ? answerValue.filter(v => v !== null && v !== undefined) : [];
              break;
            case 'SINGLE_CHOICE':
              answerValue = (answerValue === null || answerValue === undefined) ? null : String(answerValue);
              break;
            case 'TEXT_INPUT':
              answerValue = (answerValue === null || answerValue === undefined) ? null : String(answerValue);
              break;
            case 'YES_NO':
              // Converte true/false para "sim"/"não" para envio ao backend
              answerValue = (answerValue === null || answerValue === undefined) ? null : (answerValue ? 'sim' : 'não');
              break;
          }

          return {
            question_id: question.id,
            answer: answerValue,
            observation: observationValue || undefined,
          };
        });

      const answersToSend: IAnamneseSendAnswers = {
        patient_info: patientInfoValue as IPatient,
        answers: answersDto
      };

      this.anamneseService.sendAnswers(this.anamneseId || '', answersToSend)
        .then((response) => {
          this.loading = false;
          this.alreadyAnswered = true;

          // Limpar cache após envio bem-sucedido
          if (this.isPatientUser && this.anamneseId) {
            this.cacheService.clearCache(this.anamneseId);
           
          }

          // Registra evento de submissão da anamnese no HyperDX
          this.hyperDxService.logAction('anamnese-submitted', {
            userId: this.isPatientUser ? `patient-${this.anamneseId}` : this.authService.getUserId(),
            userName: this.isPatientUser ? `Paciente: ${this.patient?.full_name}` : this.authService.getUsername(),
            userEmail: this.isPatientUser ? `${this.patient?.full_name} <${this.patient?.email || 'email_nao_disponivel@westetics.com.br'}>` : this.authService.getUserEmail(),
            patientName: this.patient?.full_name || 'Paciente não identificado',
            patientEmail: this.patient?.email || 'email_nao_disponivel@westetics.com.br',
            anamneseId: this.anamneseId,
            userType: this.isPatientUser ? 'patient-submitting-anamnese' : 'admin-submitting-anamnese',
            totalQuestions: this.allQuestions.length,
            totalSections: this.sections.length,
            adminUserId: !this.isPatientUser ? this.authService.getUserId() : undefined,
            adminUserName: !this.isPatientUser ? this.authService.getUsername() : undefined,
            timestamp: new Date().toISOString()
          });

          if (this.isPatientUser) {
            this.router.navigate(['/anamnese-success']);
          } else {
            this.cancelButton();
          }
        })
        .catch(err => {
          this.loading = false;
          this.showError = true;
          console.error("Erro ao enviar anamnese:", err);
          const errorMessage = err?.error?.message || err?.message || "Ocorreu um erro ao enviar suas respostas. Tente novamente.";
          this.store.dispatch(setWarningText(errorMessage));
        });

    } catch (error) {
      this.loading = false;
      this.showError = true;
      console.error("Erro inesperado ao submeter anamnese:", error);
      this.store.dispatch(setWarningText("Ocorreu um erro inesperado. Por favor, contate o suporte."));
    }
  }

  onClickPhoneCountryButton() {
    this.isDropdownPhoneOpen = !this.isDropdownPhoneOpen;
    this.filteredPhoneCountries = this.countriesDdi;
  }

  onClickSelectPhoneCountry(country: ICountriesDDI) {
    this.anamneseForm.controls['patient.phoneCountry'].setValue(country);
    if (this.patient) {
      this.patient.phone_country_code = country.pais || '';
    }
    this.isDropdownPhoneOpen = false;
  }

  getSelectedPhoneCountry() {
    const selectedCountry = this.anamneseForm.controls['patient.phoneCountry'].value;

    if (selectedCountry && typeof selectedCountry === 'object' && selectedCountry.pais) {
      return this.countriesDdi.find(c => c.pais === selectedCountry.pais) || this.getDefaultCountryDdiObject();
    }

    if (typeof selectedCountry === 'string') {
      const found = this.countriesDdi.find(c => c.pais === selectedCountry) || this.countriesDdi.find(c => c.ddi === selectedCountry);
      if (found) return found;
    }

    return this.getDefaultCountryDdiObject();
  }

  filterPhoneCountries(event: Event) {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredPhoneCountries = this.countriesDdi.filter(country =>
      country.pais?.toLowerCase().includes(searchTerm) ||
      country.ddi?.toLowerCase().includes(searchTerm)
    );
  }

  // Métodos para o dropdown de país do telefone do responsável
  onClickGuardianPhoneCountryButton() {
    this.isDropdownGuardianPhoneOpen = !this.isDropdownGuardianPhoneOpen;
    this.filteredGuardianPhoneCountries = this.countriesDdi;
  }

  onClickSelectGuardianPhoneCountry(country: ICountriesDDI) {
    this.anamneseForm.controls['patient.guardian_phone_country'].setValue(country);
    this.isDropdownGuardianPhoneOpen = false;
  }

  getSelectedGuardianPhoneCountry() {
    const selectedCountry = this.anamneseForm.controls['patient.guardian_phone_country']?.value;

    if (selectedCountry && typeof selectedCountry === 'object' && selectedCountry.pais) {
      return this.countriesDdi.find(c => c.pais === selectedCountry.pais) || this.getDefaultCountryDdiObject();
    }

    if (typeof selectedCountry === 'string') {
      const found = this.countriesDdi.find(c => c.pais === selectedCountry) || this.countriesDdi.find(c => c.ddi === selectedCountry);
      if (found) return found;
    }

    return this.getDefaultCountryDdiObject();
  }

  filterGuardianPhoneCountries(event: Event) {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredGuardianPhoneCountries = this.countriesDdi.filter(country =>
      country.pais?.toLowerCase().includes(searchTerm) ||
      country.ddi?.toLowerCase().includes(searchTerm)
    );
  }

  onGuardianPhoneInput(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const phoneControl = this.anamneseForm.controls['patient.guardian_phone'];
    if (!phoneControl) {
      return;
    }

    const sanitizedValue = inputElement.value.replace(/\D/g, '');
    inputElement.value = sanitizedValue;
    phoneControl.setValue(sanitizedValue);
    phoneControl.updateValueAndValidity({ emitEvent: false });
  }

  onGuardianCpfInput(event: Event) {
    const control = this.anamneseForm.controls['patient.guardian_cpf'];
    if (control) {
      this.documentUtils.onCpfCnpjInput(event, control);
    }
  }

  onClickCountryButton() {
    this.isDropdownCountryOpen = !this.isDropdownCountryOpen;
    this.filteredCountries = this.countriesDdi;
  }

  onClickSelectCountry(country: ICountriesDDI) {
    this.anamneseForm.controls['patient.address.country']?.setValue(country.pais);
    this.isDropdownCountryOpen = false;
  }

  getSelectedCountry() {
    const countryName = this.anamneseForm.controls['patient.address.country']?.value;
    return this.countriesDdi.find(c => c.pais === countryName);
  }

  filterCountries(event: Event) {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredCountries = this.countriesDdi.filter(country =>
      country.pais?.toLowerCase().includes(searchTerm)
    );
  }

  onCepInput(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    let inputValue = inputElement.value;

    const isForeigner = this.anamneseForm.controls['patient.is_foreigner']?.value;
    const country = this.anamneseForm.controls['patient.address.country']?.value;
    const isBrazil = !isForeigner && this.postalCodeService.isBrazil(country);

    if (isBrazil) {
      const digits = inputValue.replace(/\D/g, '');

      if (digits.length > 5) {
        const formattedValue = this.postalCodeService.formatBrazilianCEP(digits);

        inputElement.value = formattedValue;
        this.anamneseForm.controls['patient.address.zip_code'].setValue(formattedValue);

        if (digits.length === 8) {
          this.cepOnChange();
        }
      } else {
        this.anamneseForm.controls['patient.address.zip_code'].setValue(digits);
      }
    } else {
      this.anamneseForm.controls['patient.address.zip_code'].setValue(inputValue);
    }
  }

  cepOnChange() {
    const cepValue = this.anamneseForm.controls['patient.address.zip_code'].value;
    if (!cepValue) return;

    const isForeigner = this.anamneseForm.controls['patient.is_foreigner']?.value;
    const country = this.anamneseForm.controls['patient.address.country']?.value;
    const isBrazil = !isForeigner && this.postalCodeService.isBrazil(country);

    if (!isBrazil) return;

    const cep = cepValue.replace(/\D/g, '');
    if (cep.length !== 8) return;

    this.disableAddressFields();

    this.postalCodeService.fetchAddressForBrazilianCEP(cep)
      .then(response => {
        if (response) {
          this.anamneseForm.controls['patient.address.street'].setValue(response.logradouro || '');
          this.anamneseForm.controls['patient.address.neighborhood'].setValue(response.bairro || '');
          this.anamneseForm.controls['patient.address.city'].setValue(response.localidade || '');
          this.anamneseForm.controls['patient.address.state'].setValue(response.uf || '');
        }
      })
      .finally(() => {
        this.enableAddressFields();
      });
  }

  private disableAddressFields() {
    const addressFields = [
      'patient.address.street',
      'patient.address.complement',
      'patient.address.neighborhood',
      'patient.address.city',
      'patient.address.state'
    ];

    addressFields.forEach(field => {
      this.anamneseForm.controls[field]?.disable();
    });
  }

  private enableAddressFields() {
    const addressFields = [
      'patient.address.street',
      'patient.address.complement',
      'patient.address.neighborhood',
      'patient.address.city',
      'patient.address.state'
    ];

    addressFields.forEach(field => {
      this.anamneseForm.controls[field]?.enable();
    });
  }

  getDefaultCountryDdiObject() {
    const brasilObj = this.countriesDdi.find(country => country.pais === 'Brasil');
    if (brasilObj) {
      brasilObj.img = `https://flagcdn.com/w40/br.png`;
      return brasilObj;
    }
    return {
      pais: 'Brasil',
      img: 'https://flagcdn.com/w40/br.png',
      ddi: '+55'
    };
  }

  onPhoneInput(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const phoneControl = this.anamneseForm.controls['patient.phone'];
    if (!phoneControl) {
      return;
    }

    const sanitizedValue = inputElement.value.replace(/\D/g, '');
    inputElement.value = sanitizedValue;
    phoneControl.setValue(sanitizedValue);
    phoneControl.updateValueAndValidity({ emitEvent: false });
  }

  private getFieldDisplayName(fieldKey: string): string {
    const fieldNames: { [key: string]: string } = {
      'patient.full_name': 'Nome Completo',
      'patient.email': 'E-mail',
      'patient.phone': 'Telefone',
      'patient.birth_date': 'Data de Nascimento',
      'patient.marital_status': 'Estado Civil',
      'patient.profession': 'Profissão',
      'patient.rg': 'RG',
      'patient.cpf': 'CPF',
      'patient.image_auth': 'Permite o uso de imagem',
      'patient.instagram_account': 'Conta do Instagram',
      'patient.address.street': 'Rua',
      'patient.address.number': 'Número',
      'patient.address.complement': 'Complemento',
      'patient.address.neighborhood': 'Bairro',
      'patient.address.city': 'Cidade',
      'patient.address.state': 'Estado',
      'patient.address.zip_code': 'CEP',
      'patient.address.country': 'País',
      'patient.guardian_name': 'Nome do Responsável',
      'patient.guardian_cpf': 'CPF do Responsável',
      'patient.guardian_phone': 'Telefone do Responsável',
      'patient.guardian_phone_country': 'DDI do Telefone do Responsável'
    };

    return fieldNames[fieldKey] || fieldKey;
  }

  onFieldBlur(fieldKey: string) {
    const control = this.anamneseForm.controls[fieldKey];
    const initialValue = this.initialValues[fieldKey];
    const isForeigner = this.anamneseForm.controls['patient.is_foreigner'].value;

    if (control && initialValue) {
      const currentValue = control.value;
      if (!currentValue || currentValue.toString().trim() === '') {
     
        control.setValue(initialValue, { emitEvent: false });
        control.markAsTouched();

        const fieldName = this.getFieldDisplayName(fieldKey);
        this.store.dispatch(setWarningText(`O campo ${fieldName} não pode ficar em branco pois já possui um valor cadastrado.`));
      }
    } else if (control && control.hasValidator(Validators.required) && !((fieldKey === 'patient.rg' || fieldKey === 'patient.cpf') && isForeigner)) {
      const currentValue = control.value;
      if (!currentValue || currentValue.toString().trim() === '') {
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    }
  }

  onCpfCnpjInput(event: Event) {
    const control = this.anamneseForm.controls['patient.cpf'];
    if (control) {
      this.documentUtils.onCpfCnpjInput(event, control);
    }
  }

  onBirthDateInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    const numericValue = value.replace(/\D/g, '');

    let formattedValue = '';
    if (numericValue.length > 0) {
      formattedValue = numericValue.substring(0, Math.min(2, numericValue.length));
      if (numericValue.length > 2) {
        formattedValue += '/' + numericValue.substring(2, Math.min(4, numericValue.length));
        if (numericValue.length > 4) {
          formattedValue += '/' + numericValue.substring(4, Math.min(8, numericValue.length));
        }
      }
    }

    if (numericValue.length === 8) {
      const day = numericValue.substring(0, 2);
      const month = numericValue.substring(2, 4);
      const year = numericValue.substring(4, 8);

      const isValidDate = this.isValidDate(parseInt(day), parseInt(month), parseInt(year));

      if (isValidDate) {
        const isoDate = `${year}-${month}-${day}`;
        this.anamneseForm.controls['patient.birth_date'].setValue(formattedValue, { emitEvent: false });

        this.initialValues['patient.birth_date_iso'] = isoDate;

        // Atualiza validadores do responsável quando a data de nascimento é preenchida completamente
        this.updateGuardianValidators(formattedValue);
      }
    } else {
      // Se a data estiver incompleta, reseta o estado de menor de idade
      this.isMinor = false;
    }
  }

  // Método para capturar autopreenchimento do navegador
  onBirthDateChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Tenta processar diferentes formatos de data (do autopreenchimento)
    let numericValue = value.replace(/\D/g, '');

    // Se o valor tem 8 dígitos, processa como data
    if (numericValue.length === 8) {
      const day = numericValue.substring(0, 2);
      const month = numericValue.substring(2, 4);
      const year = numericValue.substring(4, 8);

      const isValidDate = this.isValidDate(parseInt(day), parseInt(month), parseInt(year));

      if (isValidDate) {
        const formattedValue = `${day}/${month}/${year}`;
        this.anamneseForm.controls['patient.birth_date'].setValue(formattedValue, { emitEvent: false });
        this.updateGuardianValidators(formattedValue);
      }
    } else if (value.includes('/') && value.length >= 10) {
      // Se já está formatado como DD/MM/AAAA
      this.updateGuardianValidators(value);
    }
  }

  private isValidDate(day: number, month: number, year: number): boolean {
    if (month < 1 || month > 12) return false;

    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) return false;

    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear + 1) return false;

    return true;
  }

  updatePostalCodePlaceholder() {
    const isForeigner = this.anamneseForm.controls['patient.is_foreigner']?.value;
    const country: string = this.anamneseForm.controls['patient.address.country']?.value;
    const isBrazil = !isForeigner && this.postalCodeService.isBrazil(country);

    const cepField = document.getElementById('zipCode') as HTMLInputElement;
    if (cepField) {
      if (isBrazil) {
        cepField.setAttribute('placeholder', 'Digite o CEP (00000-000)');
      } else {
        cepField.setAttribute('placeholder', 'Digite o código postal/ZIP code');
      }
    }
  }

  // Método para lidar com a seleção de múltiplas opções (checkboxes)
  onMultipleChoiceChange(question_id: string, optionValue: string, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    const formControl = this.anamneseForm.controls[question_id + 'answer'];
    if (!formControl) return;

    // Garantir que o valor é um array
    let currentValues: string[] = Array.isArray(formControl.value) ? formControl.value : [];

    if (isChecked) {
      // Adicionar valor se não existir
      if (!currentValues.includes(optionValue)) {
        currentValues.push(optionValue);
      }
    } else {
      // Remover valor se existir
      currentValues = currentValues.filter(value => value !== optionValue);
    }

    // Atualizar o FormControl
    formControl.setValue(currentValues);
    formControl.markAsDirty();
    formControl.updateValueAndValidity();

    // Atualizar validação da observação se necessário
    const question = this.allQuestions.find(q => q.id === question_id);
    if (question && question.options) {
      this.updateObservationValidation(question_id, question.type, question.options, currentValues);
    }
  }

  // Verifica se uma opção está selecionada em uma pergunta MULTIPLE_CHOICE
  isOptionSelected(question_id: string, optionValue: string): boolean {
    const formControl = this.anamneseForm.controls[question_id + 'answer'];
    if (!formControl) return false;

    const values = Array.isArray(formControl.value) ? formControl.value : [];
    return values.includes(optionValue);
  }

  // Método específico para verificar se a opção selecionada em SINGLE_CHOICE tem triggersFreeText
  hasSingleChoiceOptionWithFreeText(question_id: string, options: AnamneseQuestionOptionDto[]): boolean {
    if (!options || !Array.isArray(options) || options.length === 0) return false;

    const formControl = this.anamneseForm.controls[question_id + 'answer'];
    if (!formControl || !formControl.value) return false;

    const selectedValue = formControl.value;
    const selectedOption = options.find(o => o.value === selectedValue);
    return selectedOption?.triggersFreeText === true;
  }

  // Verifica se alguma opção selecionada tem triggersFreeText=true
  hasSelectedOptionWithFreeText(question_id: string, options: AnamneseQuestionOptionDto[]): boolean {
    if (!options || !Array.isArray(options) || options.length === 0) return false;

    const formControl = this.anamneseForm.controls[question_id + 'answer'];
    if (!formControl || !formControl.value) return false;

    // Para MULTIPLE_CHOICE
    const selectedValues = Array.isArray(formControl.value) ? formControl.value : [];
    if (selectedValues.length === 0) return false;

    return options.some(option =>
      selectedValues.includes(option.value) && option.triggersFreeText === true
    );
  }

  // Obter a label de uma opção SINGLE_CHOICE a partir do value
  getSingleChoiceLabel(question_id: string, options: AnamneseQuestionOptionDto[]): string {
    if (!options || !Array.isArray(options) || options.length === 0) return '';

    const value = this.getQuestionAnswer(question_id);
    if (!value) return '';

    const selectedOption = options.find(o => o.value === value);
    return selectedOption?.label || value;
  }

  // Obter as labels de todas as opções selecionadas em uma pergunta MULTIPLE_CHOICE
  getMultipleChoiceLabels(question_id: string, options: AnamneseQuestionOptionDto[]): string[] {
    if (!options || !Array.isArray(options) || options.length === 0) return [];

    const values = this.getQuestionAnswer(question_id);
    if (!values || !Array.isArray(values) || values.length === 0) return [];

    return values.map(value => {
      const option = options.find(o => o.value === value);
      return option?.label || value;
    });
  }

  // Verifica se a resposta é um booleano (para YES_NO), fornecendo um valor padrão em caso de null
  getYesNoValue(question_id: string): boolean | null {
    const answer = this.getQuestionAnswer(question_id);
    return answer === null ? null : !!answer;
  }

  // Step navigation methods
  nextStep(): void {
    if (this.isStepValid(this.currentStep)) {
      // Salvar no cache antes de avançar
      if (this.isPatientUser && this.anamneseId) {
        this.saveToCache();
      }

      if (this.currentStep < this.totalSteps - 1) {
        this.currentStep++;
        window.scrollTo(0, 0); // Scroll to top only if step is valid and we are advancing
      } else {
        // Last step, try to submit
        this.submitAnamnese();
      }
    } else {
      // Mark fields as touched to show errors for the current step
      this.markStepControlsAsTouched(this.currentStep);
      this.store.dispatch(setWarningText("Por favor, preencha todos os campos obrigatórios da etapa atual antes de prosseguir."));
      // Optionally, scroll to the first invalid field in the current step
      this.scrollToFirstInvalidFieldOfStep(this.currentStep);
    }
  }

  previousStep(): void {
    // Salvar no cache antes de voltar
    if (this.isPatientUser && this.anamneseId) {
      this.saveToCache();
    }

    window.scrollTo(0, 0);
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    // Salvar no cache antes de mudar de step
    if (this.isPatientUser && this.anamneseId) {
      this.saveToCache();
    }

    window.scrollTo(0, 0);
    if (step >= 0 && step < this.totalSteps) {
      if (step < this.currentStep) { // Allow going back without validation
        this.currentStep = step;
      } else { // Validate current and intermediate steps before jumping forward
        let canProceed = true;
        for (let i = this.currentStep; i < step; i++) {
          if (!this.isStepValid(i)) {
            canProceed = false;
            this.markStepControlsAsTouched(i);
            this.store.dispatch(setWarningText(`Por favor, preencha todos os campos obrigatórios da etapa ${i + 1} antes de prosseguir.`));
            this.scrollToFirstInvalidFieldOfStep(i);
            this.currentStep = i; // Stay on the invalid step
            break;
          }
        }
        if (canProceed) {
          this.currentStep = step;
        }
      }
    }
  }

  isStepValid(stepIndex: number): boolean {
    const controlsToValidate: AbstractControl[] = [];
    let stepContext = ""; // Para o log


    if (stepIndex === 0) { // Patient data step
      stepContext = "Patient Data (Step 0)";
      
      Object.keys(this.anamneseForm.controls).forEach(key => {
        // Log every key being checked for patient data step
        
        if (key.startsWith('patient.')) {
          const control = this.anamneseForm.controls[key]; // Usando .controls[key] para consistência com submitAnamnese
          if (control) {
            controlsToValidate.push(control);
          } else {
            console.warn(`[isStepValid] Step 0 - Control for key ${key} is null/undefined.`);
          }
        }
      });
      
    } else if (this.sections && stepIndex > 0 && stepIndex <= this.sections.length) { // Section steps
      const sectionIndex = stepIndex - 1;
      // Adicionando verificação para garantir que this.sections[sectionIndex] existe
      if (this.sections[sectionIndex]) {
        stepContext = `Section ${this.sections[sectionIndex].name} (Step ${stepIndex})`;
        
        const section = this.sections[sectionIndex];
        if (section && section.questions) {
          section.questions.forEach(question => {
            const answerControlName = question.id + 'answer';
            const observationControlName = question.id + 'observation';

            const answerControl = this.anamneseForm.controls[answerControlName];
            const observationControl = this.anamneseForm.controls[observationControlName];

            if (answerControl) {
              controlsToValidate.push(answerControl);
            }

            if (observationControl) {
              controlsToValidate.push(observationControl);
            }
          });
        }
      } else {
        console.warn(`[isStepValid] Attempted to validate a section at stepIndex ${stepIndex}, but section data is missing.`);
        return true; // Ou false, dependendo do comportamento desejado
      }
    } else {
      console.warn(`[isStepValid] Called with invalid stepIndex: ${stepIndex}`);
      return true; // Defaulting to true for unknown step index to avoid blocking unnecessarily
    }

    if (controlsToValidate.length === 0 && stepIndex > 0) {
      return true;
    }
    if (controlsToValidate.length === 0 && stepIndex === 0) {
      console.warn(`[isStepValid] No patient controls found for validation at Step 0. This is unexpected.`);
      return false; // Se não há controles de paciente, a etapa 0 não pode ser válida.
    }

    let overallStepIsValid = true;

    for (const control of controlsToValidate) {
      let controlName = 'Unknown Control';
      const formControls = this.anamneseForm.controls;
      for (const key in formControls) {
        if (formControls[key] === control) {
          controlName = key;
          break;
        }
      }

      // Forçamos a atualização da validade do controle ANTES de checar seu estado.
      // Usamos { emitEvent: false } para evitar loops caso haja valueChanges complexos.
      control.updateValueAndValidity({ emitEvent: false });
      const isInvalid = control.invalid; // Checamos o estado APÓS a atualização.


      if (isInvalid) {
        overallStepIsValid = false;
      }
    }

    return overallStepIsValid;
  }

  private markStepControlsAsTouched(stepIndex: number): void {
    if (stepIndex === 0) { // Patient data step
      Object.keys(this.anamneseForm.controls).forEach(key => {
        if (key.startsWith('patient.')) {
          this.anamneseForm.controls[key]?.markAsTouched();
        }
      });
    } else if (this.sections && stepIndex > 0 && stepIndex <= this.sections.length) { // Section steps
      const section = this.sections[stepIndex - 1];
      if (section && section.questions) {
        section.questions.forEach(question => {
          this.anamneseForm.controls[question.id + 'answer']?.markAsTouched();
          this.anamneseForm.controls[question.id + 'observation']?.markAsTouched(); // Marcar observações como touched para validação
        });
      }
    }
  }

  private scrollToFirstInvalidFieldOfStep(stepIndex: number): void {
    let firstInvalidControlKey: string | null = null;

    if (stepIndex === 0) { // Patient data step
      const patientKeys = Object.keys(this.anamneseForm.controls).filter(key => key.startsWith('patient.'));
      for (const key of patientKeys) {
        const control = this.anamneseForm.controls[key];
        if (control && control.invalid) {
          firstInvalidControlKey = key.split('.').pop() || key; // Get the last part for ID
          break;
        }
      }
    } else if (this.sections && stepIndex > 0 && stepIndex <= this.sections.length) { // Section steps
      const section = this.sections[stepIndex - 1];
      if (section && section.questions) {
        for (const question of section.questions) {
          const answerControl = this.anamneseForm.controls[question.id + 'answer'];
          const observationControl = this.anamneseForm.controls[question.id + 'observation'];

          if (answerControl && answerControl.invalid) {
            firstInvalidControlKey = question.id; // Use a ID do controle de resposta
            break;
          }

          if (observationControl && observationControl.invalid) {
            firstInvalidControlKey = question.id; // Use a ID do controle de observação
            break;
          }
        }
      }
    }
    if (firstInvalidControlKey) {
      this.scrollToFirstInvalidField(firstInvalidControlKey);
    }
  }

  getProgressPercentage(): number {
    if (this.totalSteps <= 1) { // Avoid division by zero or progress for a single step
      return this.currentStep > 0 ? 100 : 0;
    }
    return (this.currentStep / (this.totalSteps - 1)) * 100;
  }

  /**
   * Configura o cache automático para salvar respostas conforme o usuário preenche
   */
  private setupAutomaticCache(): void {
    if (!this.isPatientUser || !this.anamneseId) {
      return;
    }

    // Cancelar subscription anterior se existir
    if (this.cacheSubscription) {
      this.cacheSubscription.unsubscribe();
    }

    // Monitorar mudanças no formulário com debounce para evitar muitas gravações
    this.cacheSubscription = this.anamneseForm.valueChanges
      .pipe(
        debounceTime(500), // Aguardar 500ms após última mudança
        distinctUntilChanged() // Só salvar se houve mudança real
      )
      .subscribe(() => {
        this.saveToCache();
      });

  }

  /**
   * Salva o estado atual do formulário no cache
   */
  private saveToCache(): void {
    if (!this.isPatientUser || !this.anamneseId || this.alreadyAnswered) {
      return;
    }

    try {
      const formData = this.anamneseForm.getRawValue();

      // Adicionar informações extras para o cache
      const cacheData = {
        ...formData,
        currentStep: this.currentStep,
        timestamp: Date.now()
      };

      this.cacheService.saveToCache(this.anamneseId, cacheData);
    } catch (error) {
      console.error('[AnamneseAnswers] Erro ao salvar no cache:', error);
    }
  }

  /**
   * Carrega dados do cache se disponível e válido
   */
  private loadFromCacheIfAvailable(): void {
    if (!this.isPatientUser || !this.anamneseId || this.hasLoadedFromCache) {
      return;
    }

    try {
      const cachedData = this.cacheService.getFromCache(this.anamneseId);
      if (cachedData) {
        const { currentStep, timestamp, ...formData } = cachedData;

        Object.keys(formData).forEach(key => {
          const control = this.anamneseForm.controls[key];
          if (control) {
            control.setValue(formData[key]);
          }
        });

        if (typeof currentStep === 'number' && currentStep >= 0 && currentStep < this.totalSteps) {
          this.currentStep = currentStep;
        }

        this.hasLoadedFromCache = true;
        this.cdRef.detectChanges();

        // Mostrar informação sobre cache restaurado
        const cacheInfo = this.cacheService.getCacheInfo(this.anamneseId);
        if (cacheInfo) {
          const timeAgo = Math.round((Date.now() - cacheInfo.timestamp) / 1000 / 60); // minutos
        }
      }

  // Garantir normalização após restauração do cache
  this.normalizePhoneCountryControl();
    } catch (error) {
      console.error('[AnamneseAnswers] Erro ao carregar do cache:', error);
    }
  }

  /**
   * Método chamado quando o componente é destruído
   */
  ngOnDestroy(): void {
    // Cancelar subscription do cache
    if (this.cacheSubscription) {
      this.cacheSubscription.unsubscribe();
      this.cacheSubscription = null;
    }

    // Salvar estado final no cache se for usuário paciente
    if (this.isPatientUser && this.anamneseId && !this.alreadyAnswered) {
      this.saveToCache();
    }
  }
}
